import React, { useEffect, useId, useState } from 'react';

import { color } from '@strava/ui-tokens/js';

interface SubscriberShieldProps {
  src: string;
  size?: 'xlarge' | 'large' | 'default' | 'small' | 'xsmall';
  alt?: string;
  title?: string;
}
export const SubscriberShield = ({
  src,
  size = 'large',
  alt = '',
  title,
  ...otherProps
}: SubscriberShieldProps) => {
  const sizeValues = {
    xlarge: {
      height: 124,
      width: 124,
      strokeWidth: 6.07843,
      x1: -39.6129,
      x2: 36.668,
      y1: -27.6128,
      y2: 150.122,
      transform: '0.00425781 0 0 0.00390625 -0.045 0',
      translate: '0, 1.0501',
      d: 'M106.96 3.96094C111.422 3.96094 115.039 7.57825 115.039 12.04V90.1924C115.039 93.2697 113.292 96.0712 110.551 97.4307L110.282 97.5576L67.9111 116.668C64.2699 118.31 60.1156 118.361 56.4424 116.821L56.0889 116.668L13.7178 97.5576C10.8221 96.2516 8.96094 93.3689 8.96094 90.1924V12.04C8.96094 7.57825 12.5782 3.96094 17.04 3.96094H106.96Z'
    },
    large: {
      height: 64,
      width: 64,
      strokeWidth: 2.5,
      x1: -23.823,
      x2: 36.668,
      y1: -14.783,
      y2: 76.122,
      transform: '.00429 0 0 .0039 -.049 0',
      translate: '4, 1.229',
      d: 'M51.428 1.75a3.323 3.323 0 0 1 3.322 3.322V46.08c0 1.222-.67 2.337-1.73 2.916l-.218.11-22.356 10.157a5.914 5.914 0 0 1-4.598.124l-.294-.124L3.198 49.105a3.323 3.323 0 0 1-1.948-3.026V5.072A3.323 3.323 0 0 1 4.572 1.75h46.856Z'
    },
    default: {
      height: 48,
      width: 48,
      strokeWidth: 2.5,
      x1: -15.83,
      x2: 29.202,
      y1: -10.099,
      y2: 55.665,
      transform: '.00441 0 0 .0039 -.065 0',
      translate: '2.75, 0.55',
      d: 'M38 1.75a2.75 2.75 0 0 1 2.75 2.75v30.26a2.75 2.75 0 0 1-1.408 2.401l-.177.091-15.956 7.462a4.625 4.625 0 0 1-3.684.101l-.234-.101-15.956-7.462a2.75 2.75 0 0 1-1.585-2.491V4.5A2.75 2.75 0 0 1 4.5 1.75H38Z'
    },
    small: {
      height: 32,
      width: 32,
      strokeWidth: 1.5,
      x1: -10.9032,
      x2: 19.5692,
      y1: -6.89135,
      y2: 38.0042,
      transform: '0.004375 0 0 0.00390625 -0.06 0',
      translate: '1.5, 0.115',
      d: 'M26 1.25C26.9665 1.25 27.75 2.0335 27.75 3V23.5684C27.7499 24.2072 27.4024 24.7919 26.8496 25.0986L26.7363 25.1562L15.7617 30.2422C15.0116 30.5898 14.1532 30.6118 13.3896 30.3076L13.2383 30.2422L2.26367 25.1562C1.64548 24.8696 1.25007 24.2498 1.25 23.5684V3C1.25 2.0335 2.0335 1.25 3 1.25H26Z'
    },
    xsmall: {
      height: 24,
      width: 24,
      strokeWidth: 1.25,
      x1: -7.54839,
      x2: 15.0459,
      y1: -4.58913,
      y2: 28.6513,
      transform: '0.00438133 0 0 0.00390625 -0.0608108 0',
      translate: '0.75, -0.289',
      d: 'M19.75 1.375C20.5094 1.375 21.125 1.99061 21.125 2.75V17.9785C21.1249 18.4466 20.8873 18.8775 20.5029 19.1289L20.3291 19.2256L12.2236 22.9873C11.6833 23.2381 11.0702 23.2692 10.5117 23.0811L10.2764 22.9873L2.1709 19.2256C1.68556 19.0003 1.37514 18.5136 1.375 17.9785V2.75C1.375 1.99061 1.99061 1.375 2.75 1.375H19.75Z'
    }
  };

  const shieldID = useId();
  const [showFallback, setShowFallback] = useState(false);
  useEffect(() => {
    if (src === 'avatar/athlete/large.png') {
      setShowFallback(true);
    }
  }, [src]);

  return (
    <svg
      fill="none"
      viewBox={`0 0 ${sizeValues[size].width} ${sizeValues[size].height}`}
      role="img"
      {...otherProps}
    >
      {title && <title id={shieldID} title={title} />}
      {alt && <desc id={shieldID}>{alt}</desc>}
      <path
        fill={`url(#${shieldID}-imageContainer)`}
        stroke={`url(#${shieldID}-gradient)`}
        strokeWidth={sizeValues[size].strokeWidth}
        d={sizeValues[size].d}
        transform={`translate(${sizeValues[size].translate})`}
      />
      <defs>
        <linearGradient
          id={`${shieldID}-gradient`}
          x1={sizeValues[size].x1}
          x2={sizeValues[size].x2}
          y1={sizeValues[size].y1}
          y2={sizeValues[size].y2}
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor={color.colorCoreWhite} />
          <stop offset={0.5} stopColor={color.colorCoreO3} />
          <stop offset={1} stopColor={color.colorExpandedMaroon} />
        </linearGradient>
        <pattern
          id={`${shieldID}-imageContainer`}
          width={1}
          height={1}
          fill={color.colorCoreO3}
          patternContentUnits="objectBoundingBox"
        >
          <use
            xlinkHref={`#${shieldID}-image`}
            transform={`matrix(${sizeValues[size].transform}) ${showFallback ? 'translate(-15,-8) scale(2.35, 2.3)' : ''}`}
          />
        </pattern>
        {showFallback ? (
          <svg
            width="256"
            height="256"
            viewBox="0 0 256 256"
            fill="none"
            id={`${shieldID}-image`}
          >
            <path
              d="M112.375 92.4582V9.6875C112.375 7.5474 110.64 5.8125 108.5 5.8125H15.5C13.3599 5.8125 11.625 7.5474 11.625 9.6875V92.4582C11.625 93.9696 12.5038 95.3431 13.8761 95.9765L58.3463 116.501C60.6646 117.571 63.3354 117.571 65.6537 116.501L110.124 95.9765C111.496 95.3431 112.375 93.9696 112.375 92.4582Z"
              fill={`url(#${shieldID}-fallback-image-pattern)`}
            />
            <defs>
              <pattern
                id={`${shieldID}-fallback-image-pattern`}
                patternContentUnits="objectBoundingBox"
                width="1"
                height="1"
              >
                <use
                  href={`#${shieldID}-fallback-image`}
                  transform="matrix(0.00435697 0 0 0.00390625 -0.0576923 0)"
                />
              </pattern>
              <image
                id={`${shieldID}-fallback-image`}
                width="256"
                height="256"
                preserveAspectRatio="none"
                href="data:image/png;base64,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"
              />
            </defs>
          </svg>
        ) : (
          <image
            id={`${shieldID}-image`}
            href={src}
            width={256}
            height={256}
            onError={() => {
              setShowFallback(true);
            }}
            preserveAspectRatio={'none'}
          />
        )}
      </defs>
    </svg>
  );
};
